import { lazy, Suspense } from "react";
import { Routes, Route, useParams, useLocation } from "react-router-dom";
import { AppNavigate } from "./lib/route-primitives";
import { useTranslation } from "react-i18next";
import { WeddingProvider, useWedding } from "./contexts/wedding-context";
import { AuthProvider, useAuth } from "./contexts/auth-context";
import { paths } from "./lib/paths";
import { Layout } from "./components/layout";
import { buildRouteElements } from "./lib/build-routes";
import {
  WEB_PUBLIC_WEDDING_ROUTES,
  WEB_PROTECTED_WEDDING_ROUTES,
} from "./lib/wedding-routes";
import { DemoPage } from "./pages/demo";
import { Heart } from "lucide-react";

// Pazarlama sitesi ayrı chunk — misafir uygulamasının bundle'ına binmesin.
const LandingPage = lazy(() => import("./pages/landing"));
const KvkkPage = lazy(() => import("./pages/legal/KvkkPage"));
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const CheckoutPage = lazy(() => import("./pages/checkout/CheckoutPage"));

/** Landing chunk'ı gelene kadar kâğıt zemin (index.html `lp-boot` ile aynı renk). */
function LandingFallback() {
  return <div className="fixed inset-0" style={{ background: "#f6f5f3" }} aria-hidden="true" />;
}

function WeddingGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { isLoading, error } = useWedding();
  const { pathname } = useLocation();
  // Premium join themes are dark and mount their own (dark) loader. During the
  // wedding fetch we don't yet know the theme, so show a neutral dark bridge for
  // /join instead of the light generic spinner — otherwise F5 flashes a light
  // spinner before the dark theme loader.
  const isJoin = pathname.endsWith("/join");

  if (isLoading) {
    if (isJoin) {
      return <div className="fixed inset-0" style={{ background: "#0a0a0a" }} aria-hidden="true" />;
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4">
        <div className="text-center space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <Heart className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold">{t("web.weddingNotFound")}</h1>
          <p className="text-muted-foreground max-w-sm">
            {t("web.invalidSlug")}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { slug } = useWedding();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AppNavigate to={paths.wedding.auth(slug)} replace />;
  }

  return <>{children}</>;
}

/** Catch-all redirect: unknown sub-paths → wedding root */
function WeddingCatchAll() {
  const { slug } = useParams<{ slug: string }>();
  return <AppNavigate to={paths.wedding.home(slug ?? "")} replace />;
}

/**
 * Segment listesi `src/lib/wedding-routes.ts`'ten türer — `route-resolution`
 * testi de AYNI listeden türediği için ikisi sapamaz. Yeni bir düğün rotası
 * eklerken `wedding-routes.ts` + `paths.ts` yeterli; burası kendiliğinden
 * güncellenir.
 */
function WeddingRoutes() {
  return (
    <WeddingProvider>
      <WeddingGate>
        <AuthProvider>
          <Routes>
            {buildRouteElements(WEB_PUBLIC_WEDDING_ROUTES, (r) => <r.element />)}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {buildRouteElements(WEB_PROTECTED_WEDDING_ROUTES, (r) => <r.element />)}
            </Route>
            <Route path="*" element={<WeddingCatchAll />} />
          </Routes>
        </AuthProvider>
      </WeddingGate>
    </WeddingProvider>
  );
}

function InvalidSlugFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-muted-foreground">
        {t("web.invalidSlug")}
      </p>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      {/* Pazarlama sitesi (kök) — önceden InvalidSlugFallback'e düşüyordu */}
      <Route
        path="/"
        element={
          <Suspense fallback={<LandingFallback />}>
            <LandingPage />
          </Suspense>
        }
      />
      {/* KVKK aydınlatma metni — `privacy` slug'ı rezerve (RESERVED_SLUGS) */}
      <Route
        path={paths.privacy}
        element={
          <Suspense fallback={<LandingFallback />}>
            <KvkkPage />
          </Suspense>
        }
      />
      {/* Kullanım koşulları — `terms` slug'ı rezerve */}
      <Route
        path={paths.terms}
        element={
          <Suspense fallback={<LandingFallback />}>
            <TermsPage />
          </Suspense>
        }
      />
      {/* Online satın alma (F8b) — `checkout` slug'ı rezerve */}
      <Route
        path={paths.checkout()}
        element={
          <Suspense fallback={<LandingFallback />}>
            <CheckoutPage />
          </Suspense>
        }
      />
      {/* Standalone demo route — auth/wedding context gerektirmez */}
      <Route path="/demo/:variant" element={<DemoPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/:slug/*" element={<WeddingRoutes />} />
      <Route
        path="*"
        element={<InvalidSlugFallback />}
      />
    </Routes>
  );
}
